import React, { useEffect, useState } from 'react';
import Joyride, { ACTIONS, STATUS } from 'react-joyride';
import { useLocation } from 'react-router-dom';
import './website-tour.css';

const steps = [
  {
    target: 'body',
    placement: 'center',
    title: 'Your business starts here.',
    content: 'Find the right UAE business package, make it yours, and take the next step with confidence.',
  },
  {
    target: '.tour-search',
    title: 'Find your perfect package',
    content: 'Search business packages from the header to find options that suit your plans.',
  },
  {
    target: '.tour-plus',
    title: 'Make it your own',
    content: 'Open the package builder to explore and customize your business setup.',
  },
  {
    target: '.tour-login',
    title: 'Everything in one account',
    content: 'Sign in or create an account to manage your business journey.',
  },
  {
    target: '.tour-bestsell',
    title: 'Explore popular choices',
    content: 'Browse our best selling packages and compare options for your business.',
  },
  {
    target: '.tour-advance',
    title: 'Narrow down your search',
    content: 'Use these search options to find a package that matches your requirements.',
  },
].map(step => ({ ...step, disableBeacon: true }));

export function TourTooltip({ index, size, step, isLastStep, backProps, closeProps, primaryProps, skipProps, tooltipProps }) {
  return (
    <section {...tooltipProps} className="website-tour" aria-labelledby="website-tour-title" aria-describedby="website-tour-description">
      <div className="website-tour__accent" />
      <button {...closeProps} type="button" className="website-tour__close" aria-label="Close website tour">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
      </button>
      <div className="website-tour__body">
        <div className="website-tour__icon" aria-hidden="true">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="m12 3 2.5 6.5L21 12l-6.5 2.5L12 21l-2.5-6.5L3 12l6.5-2.5L12 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /></svg>
        </div>
        <div className="website-tour__eyebrow">A QUICK LOOK AROUND</div>
        <h2 id="website-tour-title">{step.title}</h2>
        <p id="website-tour-description">{step.content}</p>
        {index === 0 && <div className="website-tour__tags"><span>Discover</span><span>Customize</span><span>Get started</span></div>}
      </div>
      <div className="website-tour__footer">
        <div className="website-tour__progress" aria-label={`Step ${index + 1} of ${size}`}>
          <span className="website-tour__count">{String(index + 1).padStart(2, '0')} <span>/ {String(size).padStart(2, '0')}</span></span>
          <div className="website-tour__dots" aria-hidden="true">{Array.from({ length: size }, (_, i) => <span key={i} className={i === index ? 'is-current' : i < index ? 'is-complete' : ''} />)}</div>
        </div>
        <div className="website-tour__actions">
          {index === 0 ? <button {...skipProps} type="button" className="website-tour__secondary">Skip tour</button> : <button {...backProps} type="button" className="website-tour__secondary">Back</button>}
          <button {...primaryProps} type="button" className="website-tour__primary" aria-label={isLastStep ? 'Finish tour' : index === 0 ? 'Start tour' : 'Next step'}>
            {isLastStep ? 'Finish' : index === 0 ? 'Start tour' : 'Next'} <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>
    </section>
  );
}

export default function JoyrideProvider() {
  const { pathname } = useLocation();
  // Unmount the tour and its portals when leaving the home page.
  return pathname === '/' ? <HomeTour /> : null;
}

function HomeTour() {
  const [dismissed, setDismissed] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 991px)').matches);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 991px)');
    const update = () => setIsMobile(media.matches);
    update();
    if (media.addEventListener) {
      media.addEventListener('change', update);
      return () => media.removeEventListener('change', update);
    }
    media.addListener(update);
    return () => media.removeListener(update);
  }, []);

  if (isMobile || dismissed) return null;

  return (
    <Joyride
      continuous
      run
      disableOverlayClose
      tooltipComponent={TourTooltip}
      callback={({ action, status }) => {
        if (action === ACTIONS.CLOSE || status === STATUS.FINISHED || status === STATUS.SKIPPED) {
          setDismissed(true);
        }
      }}
      floaterProps={{
        styles: {
          floater: { pointerEvents: 'none' },
          floaterOpening: { pointerEvents: 'auto' },
          floaterWithAnimation: { pointerEvents: 'auto' },
          floaterClosing: { pointerEvents: 'none' },
        },
      }}
      showSkipButton
      steps={steps}
      scrollOffset={120}
      spotlightPadding={8}
      styles={{
        options: {
          arrowColor: '#ffffff',
          backgroundColor: '#ffffff',
          overlayColor: 'rgba(12, 25, 35, 0.64)',
          primaryColor: '#087f6b',
          textColor: '#182e38',
          zIndex: 10000,
        },
        spotlight: { borderRadius: 12 },
      }}
    />
  );
}
